/**
 * Variant Hover Component
 * Updates the variant value display when hovering over variant option buttons
 */

export default class SwatchHover {
  constructor() {
    this.init();
  }

  init() {
    // Initialize on page load
    this.setupVariantHover();
    
    // Re-initialize when new content is loaded (for AJAX updates)
    document.addEventListener('DOMContentLoaded', () => {
      this.setupVariantHover();
    });

    // Listen for variant picker updates that might add new elements
    document.addEventListener('variant:updated', () => {
      this.setupVariantHover();
    });

    document.addEventListener('variant:loaded', () => {
      this.setupVariantHover();
    });
  }

  /**
   * Set up hover functionality for all variant elements on the page
   */
  setupVariantHover() {
    // Find all variant pickers
    const variantPickers = document.querySelectorAll('.variant-picker');
    
    variantPickers.forEach(picker => {
      this.initializePickerHover(picker);
    });
  }

  /**
   * Initialize hover functionality for a specific variant picker
   * @param {HTMLElement} picker - The variant picker element
   */
  initializePickerHover(picker) {
    // Find all variant option fieldsets within this picker (swatches and buttons)
    const variantFieldsets = picker.querySelectorAll('.variant-option--buttons');
    
    variantFieldsets.forEach(fieldset => {
      const valueDisplay = fieldset.querySelector('.variant-option__selected-value');
      if (!valueDisplay) return;

      // Store the original selected value
      const originalValue = valueDisplay.textContent.trim();
      
      // Find all variant option buttons in this fieldset
      const variantButtons = fieldset.querySelectorAll('.variant-option__button-label');
      
      variantButtons.forEach(button => {
        const radioInput = button.querySelector('input[type="radio"]');
        if (!radioInput) return;
        
        // Add hover event listeners
        button.addEventListener('mouseenter', (event) => {
          this.handleVariantHover(event, valueDisplay);
        });
        
        button.addEventListener('mouseleave', (event) => {
          this.handleVariantLeave(event, valueDisplay, originalValue);
        });

        // Update original value when selection changes
        if (radioInput) {
          radioInput.addEventListener('change', () => {
            if (radioInput.checked) {
              const newValue = radioInput.value;
              valueDisplay.textContent = newValue;
              // Update the stored original value for future hover interactions
              button.closest('.variant-option--buttons')
                .setAttribute('data-original-value', newValue);
            }
          });
        }
      });

      // Store the original value as a data attribute for reference
      fieldset.setAttribute('data-original-value', originalValue);
    });
  }

  /**
   * Handle mouse enter on variant button
   * @param {Event} event - The mouse enter event
   * @param {HTMLElement} valueDisplay - The element displaying the variant value
   */
  handleVariantHover(event, valueDisplay) {
    const radioInput = event.target.querySelector('input');
    
    valueDisplay.textContent = radioInput.value;
    valueDisplay.classList.add('variant-option__selected-value--hover');
  }

  /**
   * Handle mouse leave on variant button
   * @param {Event} event - The mouse leave event
   * @param {HTMLElement} valueDisplay - The element displaying the variant value
   * @param {string} originalValue - The original selected value to restore
   */
  handleVariantLeave(event, valueDisplay, originalValue) {
    if (valueDisplay) {
      const fieldset = event.target.closest('.variant-option--buttons');
      const selectedLabel = fieldset.querySelector('.selected_variant');
      if (selectedLabel) {
        const selectedInput = selectedLabel.querySelector('input');
        const selectedValue = selectedInput.value;
        valueDisplay.textContent = selectedValue;
      }
      valueDisplay.classList.remove('variant-option__selected-value--hover');
    }
  }

  /**
   * Refresh hover functionality (useful for dynamic content updates)
   */
  refresh() {
    this.setupVariantHover();
  }
}

// Auto-initialize when the script loads
const swatchHover = new SwatchHover();

// Export for manual initialization if needed
export { SwatchHover };
